"use client";

import { useId, useState } from "react";
import "./ReportValueStreams.css";

/* -------------------------------------------------------------------------- */
/* Public types                                                                 */
/* -------------------------------------------------------------------------- */

export type ValueStreamType = "benefit" | "cost";

/** A single value-stream row in the list. */
export interface ValueStreamItem {
  id: string;
  /** Display name of the stream. */
  label: string;
  /** Badge number shown at the start of the row (e.g. "1", "2A"). */
  streamNumber?: string;
  /**
   * Annual dollar amount. Positive = benefit, negative = cost.
   * `null` means the projection has not produced an amount for this stream yet.
   * The `type` field controls styling independently.
   */
  value: number | null;
  type: ValueStreamType;
  /** Parent metric id (grouping key) + label (display) for benefit streams.
   * Absent on cost/legacy streams. */
  metricId?: string;
  metricLabel?: string;
  /** Optional prose description shown in the expanded detail panel. */
  description?: string;
  /**
   * Formula string with `[Token Name]` placeholders.
   * Tokens matching a key in `tokenTargets` become clickable.
   */
  formula?: string;
}

/** Maps formula token labels (without brackets) to their focus targets. */
export interface FormulaTokenTarget {
  kind: "org-field" | "assumption";
  /** ID of the target field or assumption. */
  id: string;
}

/** Record of label → focus target for formula tokens. */
export type FormulaTokenConfig = Record<string, FormulaTokenTarget>;

export interface ReportValueStreamsSummary {
  /** Low end of the annual net ROI range in dollars (e.g. 2_000_000 for $2M). */
  roiLow: number;
  /** High end of the annual net ROI range in dollars. */
  roiHigh: number;
  /** Payback period in months. */
  paybackMonths?: number;
  /** Net Year-1 value in dollars. */
  year1Net?: number;
  /** Cumulative Year-3 value in dollars. */
  year3Cumulative?: number;
}

export interface ReportMetricItem {
  id: string;
  label: string;
}

/** A dismissed value stream, shown in the "Dismissed" section for re-adding. */
export interface DismissedValueStreamItem {
  id: string;
  label: string;
  type: ValueStreamType;
}

export interface ReportValueStreamsProps {
  summary: ReportValueStreamsSummary;
  metrics?: ReportMetricItem[];
  streams: ValueStreamItem[];
  /** Group streams under metric headings + rename the section "ROI & Metrics".
   * Off by default so the shared legacy ReportSection surface is unchanged. */
  groupByMetric?: boolean;
  /** Called when a formula token is clicked. */
  onFocusToken?: (target: FormulaTokenTarget) => void;
  /** Maps formula token labels to their focus targets. */
  tokenTargets?: FormulaTokenConfig;
  /** Dismiss (soft-remove) a value stream. Renders a per-row Dismiss action. */
  onDismissStream?: (streamId: string) => void;
  /** Re-add a previously dismissed value stream. */
  onRestoreStream?: (streamId: string) => void;
  /** Dismissed streams shown in the "Dismissed (N)" section with a Re-add action. */
  dismissedStreams?: DismissedValueStreamItem[];
  /** When false, the Dismiss action is shown disabled (last active stream). */
  canDismiss?: boolean;
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function formatUSD(n: number) {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(abs / 1_000).toFixed(0)}K`;
  return `$${abs.toFixed(0)}`;
}

/** Like formatUSD but preserves a leading "−" for negative values. */
function formatSignedUSD(n: number) {
  return n < 0 ? `−${formatUSD(n)}` : formatUSD(n);
}

/**
 * Parses a formula string into alternating segments of plain text and token
 * names.  Returns an array of `{ kind: "text" | "token", value: string }`.
 */
function parseFormula(formula: string) {
  const segments: Array<{ kind: "text" | "token"; value: string }> = [];
  let cursor = 0;
  const re = /\[([^\]]+)\]/g;
  let match = re.exec(formula);
  while (match !== null) {
    if (match.index > cursor) {
      segments.push({
        kind: "text",
        value: formula.slice(cursor, match.index),
      });
    }
    segments.push({ kind: "token", value: match[1] ?? "" });
    cursor = match.index + match[0].length;
    match = re.exec(formula);
  }
  if (cursor < formula.length) {
    segments.push({ kind: "text", value: formula.slice(cursor) });
  }
  return segments;
}

/* -------------------------------------------------------------------------- */
/* Sub-components                                                               */
/* -------------------------------------------------------------------------- */

function FormulaLine({
  formula,
  tokenTargets,
  onFocusToken,
}: {
  formula: string;
  tokenTargets?: FormulaTokenConfig;
  onFocusToken?: (t: FormulaTokenTarget) => void;
}) {
  const segments = parseFormula(formula);
  return (
    <div className="rvs-formula">
      {segments.map((seg, i) => {
        if (seg.kind === "text") return <span key={i}>{seg.value}</span>;
        const target = tokenTargets?.[seg.value];
        if (!target) {
          return (
            <span
              key={i}
              className="rvs-formula-token unknown"
              title={seg.value}
            >
              [{seg.value}]
            </span>
          );
        }
        return (
          <button
            key={i}
            type="button"
            className={`rvs-formula-token kind-${target.kind}`}
            onClick={() => onFocusToken?.(target)}
            title={`Jump to: ${seg.value}`}
          >
            [{seg.value}]
          </button>
        );
      })}
    </div>
  );
}

function DismissedStreams({
  streams,
  onRestoreStream,
}: {
  streams: DismissedValueStreamItem[];
  onRestoreStream?: (streamId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rvs-dismissed">
      <button
        type="button"
        className="rvs-dismissed-head"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        Dismissed ({streams.length})
      </button>
      {open && (
        <ul className="rvs-dismissed-list">
          {streams.map((s) => (
            <li key={s.id} className="rvs-dismissed-row">
              <span className="rvs-dismissed-label">{s.label}</span>
              {onRestoreStream && (
                <button
                  type="button"
                  className="rvs-dismissed-readd"
                  onClick={() => onRestoreStream(s.id)}
                >
                  Re-add
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface StreamRowProps {
  stream: ValueStreamItem;
  isOpen: boolean;
  maxAbs: number;
  onToggle: () => void;
  onFocusToken?: (target: FormulaTokenTarget) => void;
  tokenTargets?: FormulaTokenConfig;
  onDismissStream?: (streamId: string) => void;
  canDismiss: boolean;
}

function StreamRow({
  stream,
  isOpen,
  maxAbs,
  onToggle,
  onFocusToken,
  tokenTargets,
  onDismissStream,
  canDismiss,
}: StreamRowProps) {
  const barPct =
    stream.value == null
      ? 0
      : Math.round((Math.abs(stream.value) / maxAbs) * 100);
  const badge = stream.streamNumber;

  return (
    <div
      className={[
        "rvs-stream",
        stream.type === "cost" ? "cost" : "",
        isOpen ? "open" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <button
        type="button"
        className="rvs-stream-head"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="rvs-stream-chevron" aria-hidden="true">
          <svg
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden={true}
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </span>
        {badge && <span className="rvs-stream-num">{badge}</span>}
        <span className="rvs-stream-label">{stream.label}</span>
        <span className="rvs-stream-value">
          {stream.value == null ? (
            <span className="rvs-stream-value-pending">—</span>
          ) : (
            <>
              {stream.type === "cost" ? "–" : "+"}
              {formatUSD(stream.value)}
            </>
          )}
        </span>
        <div
          className="rvs-stream-bar"
          style={{ width: 80 }}
          aria-hidden="true"
        >
          <span
            className="rvs-stream-bar-fill"
            style={{ width: `${barPct}%` }}
          />
        </div>
      </button>
      {isOpen && (
        <div className="rvs-stream-detail">
          {stream.description && <p>{stream.description}</p>}
          {stream.formula && (
            <FormulaLine
              formula={stream.formula}
              tokenTargets={tokenTargets}
              onFocusToken={onFocusToken}
            />
          )}
          {onDismissStream && (
            <div className="rvs-stream-actions">
              <button
                type="button"
                className="rvs-stream-dismiss"
                disabled={!canDismiss}
                title={
                  canDismiss
                    ? "Remove this line item from the model"
                    : "Can't dismiss the last line item"
                }
                onClick={() => onDismissStream(stream.id)}
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Main component                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Full ROI summary hero + expandable value-stream accordion with formula
 * explorer.  Entirely props-driven — no context dependencies.
 */
export function ReportValueStreams({
  summary,
  metrics = [],
  streams,
  groupByMetric = false,
  onFocusToken,
  tokenTargets,
  onDismissStream,
  onRestoreStream,
  dismissedStreams,
  canDismiss = true,
}: ReportValueStreamsProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const headingIdPrefix = useId();

  const maxAbs = streams.reduce(
    (m, s) => Math.max(m, Math.abs(s.value ?? 0)),
    1,
  );
  type Grp = {
    key: string;
    heading: string;
    subtotal: number;
    items: ValueStreamItem[];
  };
  const metricGroups: Grp[] = [];
  const byKey = new Map<string, Grp>();
  let otherGroup: Grp | undefined;
  let costsGroup: Grp | undefined;
  if (groupByMetric) {
    const seenMetricIds = new Set<string>();
    for (const metric of metrics) {
      if (seenMetricIds.has(metric.id)) continue;
      seenMetricIds.add(metric.id);
      const group = {
        key: `m:${metric.id}`,
        heading: metric.label,
        subtotal: 0,
        items: [],
      };
      byKey.set(group.key, group);
      metricGroups.push(group);
    }

    for (const stream of streams) {
      let group: Grp;
      if (stream.type === "benefit" && stream.metricId) {
        const key = `m:${stream.metricId}`;
        const existing = byKey.get(key);
        if (existing) {
          group = existing;
        } else {
          group = {
            key,
            heading: stream.metricLabel ?? stream.metricId,
            subtotal: 0,
            items: [],
          };
          byKey.set(key, group);
          metricGroups.push(group);
        }
      } else if (stream.type === "cost") {
        if (!costsGroup) {
          costsGroup = {
            key: "costs",
            heading: "Investment / Costs",
            subtotal: 0,
            items: [],
          };
        }
        group = costsGroup;
      } else {
        if (!otherGroup) {
          otherGroup = {
            key: "other",
            heading: "Other",
            subtotal: 0,
            items: [],
          };
        }
        group = otherGroup;
      }
      group.items.push(stream);
      if (stream.value != null) {
        group.subtotal +=
          stream.type === "cost" ? -Math.abs(stream.value) : stream.value;
      }
    }
  }
  const groups: Grp[] = [
    ...metricGroups,
    ...(otherGroup ? [otherGroup] : []),
    ...(costsGroup ? [costsGroup] : []),
  ];

  /* ----------------------------------------------------------- ROI hero --- */
  return (
    <div className="rvs">
      {/* Section title */}
      <header>
        <h2 className="rvs-section-title">
          {groupByMetric ? "ROI & Metrics" : "ROI & Value streams"}
        </h2>
      </header>

      {/* ROI hero */}
      <div className="rvs-roi">
        <div className="rvs-roi-row">
          <span className="rvs-roi-label">Annual net ROI</span>
          <span className="rvs-roi-value">
            {formatSignedUSD(summary.roiLow)} –{" "}
            {formatSignedUSD(summary.roiHigh)}
          </span>
        </div>

        {/* Range bar */}
        <div className="rvs-rng">
          <span className="rvs-rng-edge">
            {formatSignedUSD(summary.roiLow)}
          </span>
          <div className="rvs-rng-bar">
            <div className="rvs-rng-fill" />
            <div className="rvs-rng-marker" />
          </div>
          <span className="rvs-rng-edge">
            {formatSignedUSD(summary.roiHigh)}
          </span>
        </div>

        {/* KV summary row */}
        {(summary.paybackMonths !== undefined ||
          summary.year1Net !== undefined ||
          summary.year3Cumulative !== undefined) && (
          <div className="rvs-kvs">
            {summary.paybackMonths !== undefined && (
              <div className="rvs-kv">
                <span className="rvs-kv-k">Payback</span>
                <span className="rvs-kv-v">
                  {summary.paybackMonths.toFixed(1)} mo
                </span>
              </div>
            )}
            {summary.year1Net !== undefined && (
              <div className="rvs-kv">
                <span className="rvs-kv-k">Year-1 Net</span>
                <span className="rvs-kv-v">
                  {formatSignedUSD(summary.year1Net)}
                </span>
              </div>
            )}
            {summary.year3Cumulative !== undefined && (
              <div className="rvs-kv">
                <span className="rvs-kv-k">3-Year Total</span>
                <span className="rvs-kv-v">
                  {formatSignedUSD(summary.year3Cumulative)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Streams list */}
      <div>
        <div className="rvs-stream-list">
          {groupByMetric
            ? groups.map((group) => {
                const headingId = `${headingIdPrefix}-${group.key}`;
                const hasKnownAmount = group.items.some(
                  (item) => item.value != null,
                );
                return (
                  <section
                    key={group.key}
                    aria-labelledby={headingId}
                    className="rvs-group"
                  >
                    <div className="rvs-group-head">
                      <h3 id={headingId} className="rvs-group-title">
                        {group.heading}
                      </h3>
                      {hasKnownAmount && (
                        <span>{formatSignedUSD(group.subtotal)}</span>
                      )}
                    </div>
                    {group.items.length === 0 && (
                      <div className="rvs-group-empty">Not modelled</div>
                    )}
                    {group.items.map((stream) => (
                      <StreamRow
                        key={stream.id}
                        stream={stream}
                        isOpen={openId === stream.id}
                        maxAbs={maxAbs}
                        onToggle={() =>
                          setOpenId(openId === stream.id ? null : stream.id)
                        }
                        onFocusToken={onFocusToken}
                        tokenTargets={tokenTargets}
                        onDismissStream={onDismissStream}
                        canDismiss={canDismiss}
                      />
                    ))}
                  </section>
                );
              })
            : streams.map((stream) => (
                <StreamRow
                  key={stream.id}
                  stream={stream}
                  isOpen={openId === stream.id}
                  maxAbs={maxAbs}
                  onToggle={() =>
                    setOpenId(openId === stream.id ? null : stream.id)
                  }
                  onFocusToken={onFocusToken}
                  tokenTargets={tokenTargets}
                  onDismissStream={onDismissStream}
                  canDismiss={canDismiss}
                />
              ))}
        </div>

        {dismissedStreams && dismissedStreams.length > 0 && (
          <DismissedStreams
            streams={dismissedStreams}
            onRestoreStream={onRestoreStream}
          />
        )}
      </div>
    </div>
  );
}
